import { debounce } from 'lodash'
import { observer } from 'mobx-react-lite'
import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { ActivityIndicator, Image, StatusBar, TouchableOpacity, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useThrottledCallback } from 'use-debounce'
import Toast from 'react-native-toast-message'

import { Text } from '../../components'
import { MainHeader } from '../../components/MainHeader/MainHeader'
import { useCreateGame } from '../../hooks/use-create-game'
import { useStore } from '../../models'
import { GameModeTag, TournamentAccessControl, TournamentModelType } from '../../models/api-store'
import { styles } from './styles'
import { DS } from '../../design-system'
import checkImage from '../../../assets/userInfo/check.png'
import uncheckImage from '../../../assets/userInfo/uncheck.png'
import { WebWrapper } from '@/components/WebWrapper'
import { useFocusEffect, useLocalSearchParams, useRouter } from 'expo-router'
import { create } from 'zustand'
import { TournamentDifficulty } from '@ppwr/poker'
import { useConvex, useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'
import { translate } from '../../i18n'

type TournamentConfigStore = {
  tournamentName: string
  tournamentBlindsLength: string
  tournamentActionTime: string
  tournamentStartingChips: string
  tournamentDifficulty: TournamentDifficulty
  accessControl: TournamentAccessControl
  isPrivateTournament: boolean
}
export const useTournamentConfigStore = create<TournamentConfigStore | undefined>(() => undefined)

export const UserRoleScreen = observer(() => {
  const router = useRouter()
  const store = useStore()
  const setPlayKind = store.game.setPlayKind
  const setGameKind = store.game.setGameKind
  const clearCurrentTournament = store.game.clearCurrentTournament
  const setCurrentTournament = store.game.setCurrentTournament
  const teardown = store.game.teardown

  const [role, setRole] = useState<'observer' | 'player' | undefined>()

  const tournamentConfig = useTournamentConfigStore()

  const convex = useConvex()
  const params = useLocalSearchParams<{
    gameKind: GameModeTag
    code: string
    convexGameId: string
    flow?: string
    loungeId?: string
  }>()
  const resolveParam = (p: string | string[] | undefined) => (Array.isArray(p) ? p[0] : p)
  const gameKind = params?.gameKind ?? GameModeTag.default
  const manualPasscode = params?.code
  const convexGameId = params?.convexGameId as Id<'games'> | undefined

  const manualPasscodeValue = typeof manualPasscode === 'string' ? manualPasscode : undefined
  const codeForConvexLookup = manualPasscodeValue && manualPasscodeValue.length === 6 ? manualPasscodeValue : undefined
  const joinLookup = useQuery(
    api.games.queries.getGameByCode,
    codeForConvexLookup && convexGameId ? { code: codeForConvexLookup } : 'skip'
  )
  const teacherOnlyJoin = Boolean(
    convexGameId &&
      joinLookup &&
      joinLookup.gameId === convexGameId &&
      !joinLookup.canJoin &&
      (joinLookup.state === 'pregame' || joinLookup.state === 'playing')
  )
  const convexJoinLookupLoading = Boolean(convexGameId && codeForConvexLookup && joinLookup === undefined)

  useFocusEffect(
    useCallback(() => {
      teardown()
      setRole(teacherOnlyJoin ? 'observer' : undefined)
    }, [teardown, teacherOnlyJoin])
  )

  useEffect(() => {
    return () => {
      useTournamentConfigStore.setState(undefined)
    }
  }, [])

  const { loading, setLoading, createGame } = useCreateGame()

  const onPressOption = useThrottledCallback((value: typeof role) => setRole(value), 250, { leading: true })

  const onSubmitCode = debounce(async () => {
    try {
      setLoading(true)
      const userId = store.session.user?._id.toString()
      let code = manualPasscode.toLowerCase()
      if (code.length < 1 && store.share.info && store.share.info.hash && store.share.info.hash.length > 0) {
        code = store.share.info.hash
      }
      console.log('doSubmit code', code)
      console.log('doSubmit userId', userId)
      if (code && userId) {
        const game = await store.game.getGameByHash(code)
        const channelName = game.channelName
        setGameKind(game.kind as GameModeTag)

        if (channelName) {
          store.communication.joinChannel(channelName)
          if (store.game.isTournamentModeOn) {
            clearCurrentTournament()
            setCurrentTournament(game.tournament as TournamentModelType)
          }
          //Todo: Check the attributes and update. Remove ts-ignore
          //@ts-ignore
          await store.telemetry.setUser({ ...store.session.user, gameId: store.game.gameId })
          // Game Join event moved to server side
          router.replace('/game')
        }
      }
    } catch (error) {
      //setErrorMessage(true)
      console.error('doSubmit error', error)
    } finally {
      setLoading(false)
    }
  }, 250)

  const onNext = useThrottledCallback(async () => {
    const flow = resolveParam(params.flow)
    const loungeIdForScheduled = resolveParam(params.loungeId)

    if (flow === 'scheduled' && gameKind === GameModeTag.tournament) {
      if (!role) return
      setPlayKind(role)
      setGameKind(GameModeTag.tournament)
      if (loungeIdForScheduled) {
        router.push(`/game/new?loungeId=${loungeIdForScheduled}`)
      } else {
        router.push('/game/create/scheduled-tournament')
      }
      return
    }

    role && setPlayKind(role)

    // Convex game join (teacher joining via code)
    if (convexGameId) {
      try {
        setLoading(true)
        const userId = store.session.user?.auth0Id
        const userName = store.session.user?.name || 'Player'
        const mongoUserId = store.session.user?._id.toString()
        if (!userId || !mongoUserId) throw new Error('User not authenticated')

        if (role === 'observer') {
          // `joinAsTeacher` is non-throwing when the singleton teacher seat is
          // already taken by another teacher — it returns `{ seated: false }`.
          // The product currently supports only one teacher per game (see
          // `twoTeacherJoin.test.ts`). Without the fallback below, the second
          // teacher lands on the game screen as neither player nor teacher nor
          // spectator, and gets a wedged UI. Fall back to the generic spectator
          // role so they at least have a working view and a "Back to Lobby"
          // exit. Multi-teacher support (each with elevated visibility) would
          // replace this fallback with the real join.
          const result = await convex.mutation(api.games.teacherSeat.joinAsTeacher, {
            gameId: convexGameId,
            userId,
            name: userName
          })
          if (!result.seated) {
            await convex.mutation(api.games.mutations.spectateGame, {
              gameId: convexGameId,
              userId,
              name: userName
            })
            Toast.show({ type: 'info', text1: translate('userRoleScreen.teacherSeatTaken') })
          }
        } else {
          await convex.mutation(api.games.mutations.joinGame, {
            gameId: convexGameId,
            userId,
            mongoUserId,
            name: userName,
            asPlayer: true
          })
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        router.replace({ pathname: '/game/[gameId]', params: { gameId: convexGameId } } as any)
      } catch (error) {
        console.error('[UserRoleScreen] Convex join failed', error)
        const message = error instanceof Error ? error.message : String(error)
        const toastText =
          message.includes('game has finished') || message.includes('game has ended')
            ? translate('userRoleScreen.joinFailedEnded')
            : message.includes('full')
              ? translate('userRoleScreen.joinFailedFull')
              : translate('userRoleScreen.joinFailed')
        Toast.show({ type: 'error', text1: toastText })
      } finally {
        setLoading(false)
      }
      return
    }

    if (manualPasscode) {
      onSubmitCode()
    } else if (gameKind === GameModeTag.tournament && tournamentConfig) {
      const { tournamentBlindsLength, tournamentActionTime } = tournamentConfig
      store.game.setGameKind(GameModeTag.tournament)
      store.game.setRemainingTime(tournamentBlindsLength ? Number(tournamentBlindsLength) * 60 : store.game.blindTime)
      store.game.setActionTime(tournamentActionTime ? Number(tournamentActionTime) * 1000 : store.game.actionTime)

      try {
        setLoading(true)
        await createGame(
          { gameKind, accessControl: TournamentAccessControl.private },
          { ...tournamentConfig, accessControl: TournamentAccessControl.private }
        )
      } catch (error) {
        console.error(error)
        setLoading(false)
      }
    } else {
      store.game.setGameKind(GameModeTag.default)
      try {
        await createGame({ gameKind }, undefined, { asTeacher: role === 'observer' })
      } catch (error) {
        console.error(error)
        setLoading(false)
      }
    }
  }, 1000)

  const onBack = () => {
    const flow = resolveParam(params.flow)
    const loungeIdBack = resolveParam(params.loungeId)

    if (flow === 'scheduled') {
      if (loungeIdBack && router.canDismiss()) {
        router.dismissTo(`/(root)/lounges/${loungeIdBack}` as never)
      } else if (loungeIdBack) {
        router.replace(`/lounges/${loungeIdBack}`)
      } else if (router.canGoBack()) {
        router.back()
      } else {
        router.replace('/game/create/new')
      }
      return
    }

    if (manualPasscode) {
      router.replace('/(root)/(tabs)/play')
    } else {
      if (router.canGoBack()) {
        router.back()
        return
      }
      router.replace('/game/create/new')
    }
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar animated={true} barStyle="light-content" hidden={false} />
      <WebWrapper>
        <MainHeader leftPress={onBack} leftIconStyle={{ tintColor: 'white' }} hideLogo showDarkKeyIcon={false} />
        <View style={{ flex: 1, backgroundColor: '#fff', paddingHorizontal: 24 }}>
          <Text tx="userRoleScreen.question" style={styles.question} />

          <View
            style={{
              marginTop: 16,
              gap: 16
            }}
          >
            <SelectButton isSelected={role === 'observer'} onPress={() => onPressOption('observer')}>
              <Text tx="userRoleScreen.teacher" style={styles.optionText} />
            </SelectButton>
            {!teacherOnlyJoin ? (
              <SelectButton isSelected={role === 'player'} onPress={() => onPressOption('player')}>
                <Text tx="userRoleScreen.player" style={styles.optionText} />
              </SelectButton>
            ) : null}
          </View>
          {/* <TouchableOpacity onPress={() => onSelectRole('observer')}>
            <View
              style={[
                styles.optionView,
                { borderColor: playerRole === 'observer' ? '#000' : 'lightgray' },
                { marginTop: 26 }
              ]}
            >
              {playerRole === 'observer' ? (
                <Image style={styles.radioIcon} source={checkImage} />
              ) : (
                <Image style={styles.radioIcon} source={uncheckImage} />
              )}
              <Text tx="userRoleScreen.teacher" style={styles.optionText} />
            </View>
          </TouchableOpacity> */}

          {/* <TouchableOpacity onPress={() => onSelectRole('player')}>
            <View
              style={[
                styles.optionView,
                { borderColor: playerRole === 'player' ? '#000' : 'lightgray' },
                { marginTop: 15 }
              ]}
            >
              {playerRole === 'player' ? (
                <Image style={styles.radioIcon} source={checkImage} />
              ) : (
                <Image style={styles.radioIcon} source={uncheckImage} />
              )}
              <Text tx="userRoleScreen.player" style={styles.optionText} />
            </View>
          </TouchableOpacity> */}
          <TouchableOpacity
            disabled={!role || loading || convexJoinLookupLoading}
            onPress={onNext}
            style={[styles.buttonStyle, { backgroundColor: role ? DS.palette['Brand/Purple'] : 'lightgray' }]}
          >
            <View style={styles.buttonView}>
              <Text tx="common.next" style={styles.buttonText} />
              {loading ? <ActivityIndicator size="small" color="#FFFFFF" style={styles.loadingStyle} /> : null}
            </View>
          </TouchableOpacity>
        </View>
      </WebWrapper>
    </SafeAreaView>
  )
})

type SelectButtonProps = {
  onPress: () => void
  isSelected: boolean
}
const SelectButton = ({ onPress, isSelected, children }: PropsWithChildren<SelectButtonProps>) => {
  return (
    <TouchableOpacity onPress={() => onPress()}>
      <View style={[styles.optionView, { borderColor: isSelected ? '#000' : 'lightgray' }]}>
        {isSelected ? (
          <Image style={styles.radioIcon} source={checkImage} />
        ) : (
          <Image style={styles.radioIcon} source={uncheckImage} />
        )}
        {children}
        {/* <Text tx="userRoleScreen.teacher" style={styles.optionText} /> */}
      </View>
    </TouchableOpacity>
  )
}
